import Icon from '@/components/ui/AppIcon';
import { waUrl } from '@/lib/contact';
import { ICON_BUTTON_CLASS } from '@/components/common/iconButton';

interface WhatsAppLinkProps {
  /** WhatsApp number, digits only  - take it from `PHONE_LINES[n].wa`. */
  wa: string;
  /** Which line this is, e.g. 'UK'. Becomes part of the accessible name. */
  region: string;
  /** Prefilled compose text. Vary it per surface so enquiries stay attributable. */
  text: string;
  /** GA4 `cta_click` id  - Analytics.tsx picks this up by event delegation. */
  analyticsId: string;
  /** Applied to the 44px hit area. Use for margins, not for the visible chip. */
  className?: string;
  /**
   * The visible chip inside the hit area: its size, background and border.
   * Defaults to filling the full 44px. The footer passes `h-8 w-8` so the chip
   * matches the mail/phone icons beside it while the target stays 44px.
   * Hover styles need the `group-hover/wa:` prefix, since the pointer may be
   * inside the hit area but outside a smaller chip.
   */
  chipClassName?: string;
  size?: number;
  /**
   * Which surface this sits on, which sets the glyph colour. The brand green
   * (#25D366) only clears WCAG SC 1.4.11's 3:1 on dark backgrounds - it is
   * 1.98:1 on white. Light surfaces get WhatsApp's darker green instead.
   */
  tone?: 'onLight' | 'onDark';
}

// Emitted one at a time rather than layered, so there is no Tailwind
// class-order ambiguity between two same-specificity text-* utilities.
const GLYPH = {
  onLight: 'text-[#128C7E]', // 4.14:1 on white, 3.51:1 on the muted row
  onDark: 'text-[#25D366]', // 4.06:1 on the footer's bg-primary
} as const;

/**
 * Icon-only WhatsApp link. The outer element is a bare 44x44 hit area (WCAG
 * 2.5.5, and a hard requirement in CLAUDE.md); the visible chip inside it can
 * be smaller, so a surface can match its own icon sizing without shrinking the
 * target.
 *
 * Always render this as a *sibling* of the `tel:` link, never nested inside it.
 * An anchor inside an anchor is invalid HTML and browsers hoist the inner one
 * out of the outer, which is the same failure mode that had the +44 number
 * dialling India (audit F7).
 *
 * `tone` sets the glyph colour; `chipClassName` supplies the chip's size and
 * background for the surface it sits on.
 */
export default function WhatsAppLink({
  wa,
  region,
  text,
  analyticsId,
  className = '',
  chipClassName = 'h-11 w-11',
  size = 20,
  tone = 'onLight',
}: Readonly<WhatsAppLinkProps>) {
  return (
    <a
      href={waUrl(wa, text)}
      target="_blank"
      rel="noopener noreferrer"
      data-analytics-id={analyticsId}
      aria-label={`Message our ${region} line on WhatsApp`}
      className={`group/wa ${ICON_BUTTON_CLASS} ${GLYPH[tone]} ${className}`}
    >
      <span
        className={`flex shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${chipClassName}`}
      >
        <Icon name="WhatsApp" size={size} aria-hidden="true" />
      </span>
    </a>
  );
}
